import { ui } from '@procuros/datachecks';

import { normalizePath } from 'services/webedi/webedi';

export const hasSubSections = (section: ui.Section): section is ui.GlobalModificationsSection => {
  return 'sections' in section && Array.isArray((section as ui.GlobalModificationsSection).sections);
};

export const hasFields = (
  section: ui.Section,
): section is
  | ui.InfoSection
  | ui.PartiesSection
  | ui.LineItemsSection
  | ui.TransportUnitsSection
  | ui.LocationsSection
  | ui.PaymentTermsSection
  | ui.AttachmentsSection
  | ui.GlobalModificationsSubSection => {
  return 'fields' in section && Array.isArray((section as ui.InfoSection).fields);
};

/**
 * Normalizes a field path and removes wildcard prefixes.
 * @example
 * normalizeAndCleanPath('modification_groups[0]') // => 'modification_groups.0'
 * normalizeAndCleanPath('*.charges[1].amount') // => 'charges.1.amount'
 * normalizeAndCleanPath('*.nested.*.field') // => 'nested.field'
 */
export const normalizeAndCleanPath = (path: string): string => {
  return normalizePath(path).replaceAll('*.', '');
};

/**
 * Recursively checks if a field path exists within a field's structure.
 * Handles both simple fields and multi_column fields with nested structures.
 */
export const doesPathMatchField = (field: ui.Field, targetPath: string): boolean => {
  const normalizedFieldPath = normalizeAndCleanPath(field.path);

  if (field.type !== ui.FieldTypes.multi_column) {
    return normalizedFieldPath === targetPath;
  }

  return doesPathMatchMultiColumnField(field as ui.MultiColumnField, targetPath, normalizedFieldPath);
};

export const getFirstFieldFromSection = (section: ui.Section): ui.Field | undefined => {
  if (hasFields(section)) {
    return section.fields[0];
  }

  if (hasSubSections(section)) {
    const firstSubSection = section.sections[0];
    if (firstSubSection) {
      return getFirstFieldFromSection(firstSubSection);
    }
  }

  return undefined;
};

/**
 * Returns the inline enum options declared on the field at `path` within `section`.
 * Only static `type_config.options` are returned; a field that resolves its options
 * dynamically (`options_lookup_field`), a missing field, or a section without fields
 * all yield an empty array.
 */
export const getOptionsFromUIConfigEnum = (
  section: ui.Section,
  path: string,
): ui.EnumerableFieldTypeConfigWithValues['options'] => {
  if (!hasFields(section)) {
    return [];
  }

  const field = section.fields.find((candidate) => doesPathMatchField(candidate, path));
  if (field && 'type_config' in field && field.type_config && 'options' in field.type_config) {
    return field.type_config.options;
  }

  return [];
};

export const findMatchingSubSection = (
  section: ui.GlobalModificationsSection,
  fieldPath: string,
): ui.GlobalModificationsSubSection | undefined => {
  const firstPathSegment = fieldPath.split('.')[0];

  const matchingSubSection = section.sections.find((subSection) => {
    const normalizedPath = normalizeAndCleanPath(subSection.path);
    return normalizedPath === firstPathSegment;
  });

  return matchingSubSection || section.sections[0];
};

export const doesSectionContainPath = (section: ui.Section, fieldPath: string): boolean => {
  if (hasFields(section)) {
    return section.fields.some((field) => doesPathMatchField(field, fieldPath));
  }

  if (hasSubSections(section)) {
    const pathSegments = fieldPath.split('.');
    const firstPathSegment = pathSegments[0];
    const matchingSubSection = section.sections.find((subSection) => {
      const normalizedPath = normalizeAndCleanPath(subSection.path);
      return normalizedPath === firstPathSegment;
    });

    if (matchingSubSection) {
      let startIndex = 1;
      if (pathSegments.length > 1 && !isNaN(Number(pathSegments[1]))) {
        startIndex = 2;
      }
      const remainingPath = pathSegments.slice(startIndex).join('.');
      return doesSectionContainPath(matchingSubSection, remainingPath);
    }

    return false;
  }

  return false;
};

/**
 * Checks if pathSegments starts with all segments from fieldSegments.
 * @example
 * doesPathStartWithField(['multi'], ['multi', '0', 'field']) // => true
 * doesPathStartWithField(['multi', 'nested'], ['multi', 'nested', '0']) // => true
 * doesPathStartWithField(['multi'], ['other', '0', 'field']) // => false
 * doesPathStartWithField(['a', 'b', 'c'], ['a', 'b']) // => false (field longer than target)
 */
const doesPathStartWithField = (fieldSegments: Array<string>, pathSegments: Array<string>): boolean => {
  if (fieldSegments.length > pathSegments.length) {
    return false;
  }

  for (let i = 0; i < fieldSegments.length; i++) {
    if (fieldSegments[i] !== pathSegments[i]) {
      return false;
    }
  }

  return true;
};

const doesPathMatchMultiColumnField = (
  field: ui.MultiColumnField,
  targetPath: string,
  normalizedFieldPath: string,
): boolean => {
  const pathSegments = targetPath.split('.');
  const fieldSegments = normalizedFieldPath.split('.');

  if (!doesPathStartWithField(fieldSegments, pathSegments)) {
    return false;
  }

  let nextSegmentIndex = fieldSegments.length;

  if (nextSegmentIndex < pathSegments.length && !isNaN(Number(pathSegments[nextSegmentIndex]))) {
    nextSegmentIndex++;
  }

  if (nextSegmentIndex >= pathSegments.length) {
    return false;
  }

  const remainingPath = pathSegments.slice(nextSegmentIndex).join('.');

  return field.type_config.fields.some((subField: ui.Field) => {
    const normalizedSubFieldPath = normalizeAndCleanPath(subField.path);

    if (normalizedSubFieldPath === remainingPath) {
      return true;
    }

    if (remainingPath.startsWith(normalizedSubFieldPath + '.')) {
      return doesPathMatchField(subField, remainingPath);
    }

    return false;
  });
};
